import React from 'react';
import { getThemeClass } from '../../../utils/theme';
import { NAV_ITEMS, ACTION_ITEMS } from './config';
import { usePluginSync } from '../hooks/usePluginSync';

/**
 * AdminSidebar - Navigation sidebar for admin panel
 * 
 * @param {Object} props
 * @param {string} props.activeSection - Currently active section ID
 * @param {Function} props.onNavigate - Navigation callback (sectionId) => void
 * @param {string} props.theme - Theme name
 * @param {string} props.className - Additional CSS classes
 */
const AdminSidebar = ({ 
  activeSection, 
  onNavigate, 
  theme = 'dark',
  className = ''
}) => {
  const { syncPlugins, syncStatus } = usePluginSync();

  const handleSyncClick = async () => {
    try {
      await syncPlugins();
    } catch (error) {
      // Error is handled in the hook
      console.error('Sync failed:', error);
    }
  };

  return (
    <aside 
      className={`
        flex flex-col h-full
        ${getThemeClass(theme, 'panel')}
        border-r ${getThemeClass(theme, 'border')}
        ${className}
      `}
    >
      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-2">
        <ul className="space-y-1 px-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <li key={item.id}>
                <button
                  onClick={() => onNavigate(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                    text-left text-sm font-medium transition-colors
                    ${isActive 
                      ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-400 -ml-0.5 pl-3.5' 
                      : `${getThemeClass(theme, 'text.secondary')} hover:bg-slate-700/50`
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon size={18} className={isActive ? 'text-blue-400' : ''} />
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Divider */}
      <div className={`mx-4 border-t ${getThemeClass(theme, 'border')}`} />

      {/* Action Items (Sync) */}
      <div className="py-3 px-2">
        {ACTION_ITEMS.map((item) => {
          const Icon = item.icon;
          const isSyncing = item.id === 'sync' && syncStatus.isSyncing;
          
          return (
            <button
              key={item.id}
              onClick={item.id === 'sync' ? handleSyncClick : undefined}
              disabled={isSyncing}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                text-left text-sm font-medium transition-colors
                ${getThemeClass(theme, 'text.secondary')}
                hover:bg-slate-700/50
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              <Icon 
                size={18} 
                className={isSyncing ? 'animate-spin' : ''} 
              />
              <span>{isSyncing ? 'Syncing...' : item.label}</span>
            </button>
          );
        })}
      </div>
    </aside>
  );
};

export default AdminSidebar;



